import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./BlockHighlight.module.scss";

interface BlockHighlightProps {
    /** Контейнер сетки: рамки рисуются в его координатах */
    gridRef: React.RefObject<HTMLDivElement | null>;
    /** Зум сетки (CSS zoom) — экранные px делятся на него */
    zoom: number;
    /**
     * Ключи полос (data-link-end) по убыванию точности: подсвечиваются полосы первого ключа,
     * который есть на экране, — «work:<id>», а если проект свёрнут, «project:<id>»
     */
    keys: string[];
}

/** Ключ контура предложенного сдвига: proposed:work:<id> */
export const PROPOSED_PREFIX = "proposed:";

/** На сколько рамка шире полосы с каждой стороны, px */
const OUTSET = 4;

interface Frame {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * Пульсирующая рамка поверх полос объекта — куда смотреть после перехода из списка
 * «Сдвинуто по связям». Полосы меряются по DOM, как в LinksLayer: раскрытие проекта
 * (анимация Collapse) и прокрутка двигают их уже после клика.
 */
export const BlockHighlight: React.FC<BlockHighlightProps> = ({ gridRef, zoom, keys }) => {
    const [frames, setFrames] = useState<Frame[]>([]);
    const frame = useRef(0);

    const measure = useCallback(() => {
        const grid = gridRef.current;

        if (!grid) {
            return;
        }

        const origin = grid.getBoundingClientRect();
        const scale = zoom || 1;

        for (const key of keys) {
            // «proposed:work:<id>» — контур предложенного положения из запроса на согласование
            const selector = key.startsWith(PROPOSED_PREFIX)
                ? `[data-proposed="${key.slice(PROPOSED_PREFIX.length)}"]`
                : `[data-link-end="${key}"]`;
            const bars = Array.from(grid.querySelectorAll<HTMLElement>(selector))
                .map((element) => element.getBoundingClientRect())
                .filter((rect) => rect.width > 0);

            if (bars.length) {
                setFrames(
                    bars.map((rect) => ({
                        left: (rect.left - origin.left) / scale - OUTSET,
                        top: (rect.top - origin.top) / scale - OUTSET,
                        width: rect.width / scale + OUTSET * 2,
                        height: rect.height / scale + OUTSET * 2,
                    }))
                );
                return;
            }
        }

        setFrames([]);
    }, [gridRef, zoom, keys]);

    const schedule = useCallback(() => {
        window.cancelAnimationFrame(frame.current);
        frame.current = window.requestAnimationFrame(measure);
    }, [measure]);

    useLayoutEffect(() => {
        measure();
    }, [measure]);

    useEffect(() => {
        const grid = gridRef.current;

        if (!grid) {
            return;
        }

        const observer = new MutationObserver(schedule);
        observer.observe(grid, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["style", "class"],
        });
        window.addEventListener("resize", schedule);

        // раскрытие проекта идёт анимацией — дорисовываем рамку, пока она не кончится
        const settle = window.setInterval(schedule, 100);
        const stop = window.setTimeout(() => window.clearInterval(settle), 1000);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", schedule);
            window.clearInterval(settle);
            window.clearTimeout(stop);
            window.cancelAnimationFrame(frame.current);
        };
    }, [gridRef, schedule]);

    return (
        <>
            {frames.map((item, index) => (
                <div key={index} className={styles.frame} style={item} />
            ))}
        </>
    );
};
